import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Key, Loader2, AlertTriangle, Copy, Check } from "lucide-react";

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    email: string;
    full_name?: string | null;
    profiles?: {
      full_name: string | null;
    };
  } | null;
  onSuccess?: () => void;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  user,
  onSuccess,
}: ResetPasswordDialogProps) {
  const [newPassword, setNewPassword] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const resetPassword = useMutation({
    mutationFn: async (password: string) => {
      // Llamar a RPC para cambiar contraseña con hash
      const { data, error } = await api.rpc("admin_reset_password", {
        p_user_id: user?.id,
        p_new_password: password,
      });

      // Si no existe la función RPC, actualizar directamente
      if (error?.message?.includes("function") || error?.message?.includes("does not exist")) {
        // Fallback: actualizar directamente (el trigger debería hashear)
        const { error: updateError } = await api
          .from("users")
          .update({ password_hash: password })
          .eq("id", user?.id);
        
        if (updateError) throw updateError;
        return true;
      }

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Contraseña reseteada correctamente");
      setGeneratedPassword(newPassword);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al resetear contraseña");
    },
  });

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const symbols = "!@#$%";
    let password = "";
    
    // Asegurar al menos una mayúscula, una minúscula, un número y un símbolo
    password += chars.charAt(Math.floor(Math.random() * 24)); // Mayúscula
    password += chars.charAt(24 + Math.floor(Math.random() * 24)); // Minúscula
    password += chars.charAt(48 + Math.floor(Math.random() * 8)); // Número
    password += symbols.charAt(Math.floor(Math.random() * symbols.length)); // Símbolo
    
    // Completar hasta 10 caracteres
    for (let i = 0; i < 6; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Mezclar
    password = password.split("").sort(() => Math.random() - 0.5).join("");
    
    setNewPassword(password);
  };

  const handleReset = () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    resetPassword.mutate(newPassword);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword || newPassword);
      setCopied(true);
      toast.success("Contraseña copiada al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Error al copiar");
    }
  };

  const handleClose = () => {
    setNewPassword("");
    setGeneratedPassword(null);
    setCopied(false);
    onOpenChange(false);
  };

  const userName = user?.profiles?.full_name || user?.full_name || user?.email;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Resetear Contraseña
          </DialogTitle>
          <DialogDescription>
            Establece una nueva contraseña para <strong>{userName}</strong>
          </DialogDescription>
        </DialogHeader>

        {generatedPassword ? (
          // Vista después de resetear
          <div className="space-y-4 py-4">
            <Alert className="bg-green-500/10 border-green-500/20">
              <Check className="w-4 h-4 text-green-500" />
              <AlertDescription className="text-green-700 dark:text-green-400">
                Contraseña reseteada correctamente
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <div className="flex gap-2">
                <Input
                  value={generatedPassword}
                  readOnly
                  className="font-mono"
                />
                <Button variant="outline" size="icon" onClick={copyToClipboard}>
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Comparte esta contraseña de forma segura con el usuario
              </p>
            </div>
          </div>
        ) : (
          // Vista para ingresar nueva contraseña
          <div className="space-y-4 py-4">
            <Alert variant="destructive" className="bg-amber-500/10 border-amber-500/20">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                Esta acción cambiará la contraseña del usuario. Asegúrate de comunicarle la nueva contraseña.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Ingresa o genera una contraseña"
                  className="font-mono"
                />
                <Button variant="outline" onClick={generateRandomPassword}>
                  Generar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres. Usa el botón "Generar" para crear una contraseña segura.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {generatedPassword ? (
            <Button onClick={handleClose}>Cerrar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleReset}
                disabled={resetPassword.isPending || !newPassword}
                variant="destructive"
              >
                {resetPassword.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Resetear Contraseña
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}





